/**
 * CohortRetentionTable Component
 * 
 * Displays cohort retention table with color-coded cells.
 * Rows = cohorts, Columns = time intervals (Week 1, Week 2, Week 4, Month 2, Month 3, Month 6)
 * 
 * Color coding:
 * - Green (>70%): High retention
 * - Yellow (40-70%): Medium retention
 * - Red (<40%): Low retention
 * 
 * Requirements: 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 4.9, 4.10, 4.24
 */

import React from 'react';
import PropTypes from 'prop-types';
import './CohortRetentionTable.css';

const CohortRetentionTable = ({
  cohorts = [],
  loading = false,
  onCellClick = null,
  groupBy = 'month'
}) => {
  /**
   * Get color class based on retention rate
   * @param {number|null} rate - Retention rate (0-100)
   * @returns {string} CSS class name
   */
  const getColorClass = (rate) => {
    if (rate === null || rate === undefined) {
      return 'cohort-retention-table__cell--no-data';
    }
    
    if (rate > 70) {
      return 'cohort-retention-table__cell--high';
    } else if (rate >= 40) {
      return 'cohort-retention-table__cell--medium';
    } else {
      return 'cohort-retention-table__cell--low';
    }
  };

  /**
   * Format retention rate for display
   * @param {number|null} rate - Retention rate
   * @returns {string} Formatted rate
   */
  const formatRate = (rate) => {
    if (rate === null || rate === undefined) {
      return '—';
    }
    return `${rate.toFixed(1)}%`;
  };

  /**
   * Handle cell click
   * @param {string} cohortName - Cohort name
   * @param {string} period - Time period (week1, week2, etc.)
   * @param {number|null} rate - Retention rate
   */
  const handleCellClick = (cohortName, period, rate) => {
    if (onCellClick && rate !== null) {
      onCellClick(cohortName, period, rate);
    }
  };

  // Time interval columns
  const intervals = [
    { key: 'week1', label: 'Week 1' },
    { key: 'week2', label: 'Week 2' },
    { key: 'week4', label: 'Week 4' },
    { key: 'month2', label: 'Month 2' },
    { key: 'month3', label: 'Month 3' },
    { key: 'month6', label: 'Month 6' }
  ];

  // Loading state
  if (loading) {
    return (
      <div className="cohort-retention-table cohort-retention-table--loading">
        <div className="cohort-retention-table__loading">
          <div className="cohort-retention-table__spinner"></div>
          <p>Loading cohort data...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!cohorts || cohorts.length === 0) {
    return (
      <div className="cohort-retention-table cohort-retention-table--empty">
        <div className="cohort-retention-table__empty">
          <p>No cohort data available</p>
          <p className="cohort-retention-table__empty-hint">
            Cohorts will appear as users sign up and activity data is collected.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cohort-retention-table">
      <div className="cohort-retention-table__container">
        <table 
          className="cohort-retention-table__table"
          role="table"
          aria-label="Cohort retention rates table"
        >
          <thead>
            <tr>
              <th 
                scope="col" 
                className="cohort-retention-table__header cohort-retention-table__header--cohort"
              >
                Cohort ({groupBy === 'week' ? 'Week' : 'Month'})
              </th>
              <th 
                scope="col" 
                className="cohort-retention-table__header cohort-retention-table__header--size"
              >
                Size
              </th>
              {intervals.map(interval => (
                <th 
                  key={interval.key}
                  scope="col" 
                  className="cohort-retention-table__header"
                >
                  {interval.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cohorts.map((cohort, index) => (
              <tr 
                key={`${cohort.name}-${index}`}
                className="cohort-retention-table__row"
              >
                <th 
                  scope="row" 
                  className="cohort-retention-table__cell cohort-retention-table__cell--cohort"
                >
                  {cohort.name}
                </th>
                <td className="cohort-retention-table__cell cohort-retention-table__cell--size">
                  {cohort.size}
                </td>
                {intervals.map(interval => {
                  const rate = cohort.retention[interval.key];
                  const colorClass = getColorClass(rate);
                  const isClickable = onCellClick && rate !== null;
                  
                  return (
                    <td 
                      key={interval.key}
                      className={`cohort-retention-table__cell ${colorClass} ${isClickable ? 'cohort-retention-table__cell--clickable' : ''}`}
                      onClick={() => handleCellClick(cohort.name, interval.key, rate)}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      onKeyPress={(e) => {
                        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleCellClick(cohort.name, interval.key, rate);
                        }
                      }}
                      aria-label={rate !== null ? `${cohort.name} ${interval.label}: ${formatRate(rate)}` : `${cohort.name} ${interval.label}: No data`}
                    >
                      {formatRate(rate)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="cohort-retention-table__legend">
        <div className="cohort-retention-table__legend-title">Retention Rate:</div>
        <div className="cohort-retention-table__legend-items">
          <div className="cohort-retention-table__legend-item">
            <span className="cohort-retention-table__legend-color cohort-retention-table__legend-color--high"></span>
            <span className="cohort-retention-table__legend-label">High (&gt;70%)</span>
          </div>
          <div className="cohort-retention-table__legend-item">
            <span className="cohort-retention-table__legend-color cohort-retention-table__legend-color--medium"></span>
            <span className="cohort-retention-table__legend-label">Medium (40-70%)</span>
          </div>
          <div className="cohort-retention-table__legend-item">
            <span className="cohort-retention-table__legend-color cohort-retention-table__legend-color--low"></span>
            <span className="cohort-retention-table__legend-label">Low (&lt;40%)</span>
          </div>
          <div className="cohort-retention-table__legend-item">
            <span className="cohort-retention-table__legend-color cohort-retention-table__legend-color--no-data"></span>
            <span className="cohort-retention-table__legend-label">No Data</span>
          </div>
        </div>
      </div>
    </div>
  );
};

CohortRetentionTable.propTypes = {
  cohorts: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    retention: PropTypes.shape({
      week1: PropTypes.number,
      week2: PropTypes.number,
      week4: PropTypes.number,
      month2: PropTypes.number,
      month3: PropTypes.number,
      month6: PropTypes.number
    }).isRequired
  })),
  loading: PropTypes.bool,
  onCellClick: PropTypes.func,
  groupBy: PropTypes.oneOf(['week', 'month'])
};

export default CohortRetentionTable;
